// Partner Network platform console — the acquisition network's operations view:
// lead/attribution review across BOTH channels (partners + customer-user
// referrals), conversion, reward + commission approval/payout, plans, analytics.
import { fmtDate } from '../../lib/format';
import React, { useEffect, useState } from 'react';
import {
  Table, Button, Tag, message, Space, Typography, Tabs, Modal, Form, Input, InputNumber, Select, Statistic, Row, Col, Card,
} from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import * as api from '../../services/partnerNetworkService';

const { Text } = Typography;

const attrColor: Record<string, string> = {
  ACCEPTED: 'green', PENDING_REVIEW: 'gold', DUPLICATE: 'orange', ALREADY_CUSTOMER: 'blue',
  ALREADY_ASSIGNED: 'purple', REJECTED: 'red', EXPIRED: 'default', REVOKED: 'red', TRANSFERRED: 'cyan',
};
// Plain-language labels for the referral-status wire values (enum stays as-is).
const attrLabel: Record<string, string> = {
  PENDING_REVIEW: 'Pending review', ACCEPTED: 'Approved', REJECTED: 'Declined',
  DUPLICATE: 'Duplicate', ALREADY_CUSTOMER: 'Already a customer', ALREADY_ASSIGNED: 'Already assigned',
  EXPIRED: 'Expired', REVOKED: 'Revoked', TRANSFERRED: 'Transferred',
};
const referralLabel = (v?: string) => (!v ? '—' : attrLabel[v] ?? v.replace(/_/g, ' ').toLowerCase());
const channelTag = (t: string) =>
  t === 'CUSTOMER_USER' ? <Tag color="cyan">customer user</Tag> : <Tag color="geekblue">partner</Tag>;

function LeadsTab() {
  const [rows, setRows] = useState<api.PNLead[]>([]);
  const [loading, setLoading] = useState(true);
  const [attribution, setAttribution] = useState<string | undefined>();
  const [convert, setConvert] = useState<api.PNLead | null>(null);
  const [cForm] = Form.useForm();

  const load = () => {
    setLoading(true);
    api.listLeads({ attribution }).then((d) => setRows(d.items || [])).catch((e) => message.error(e?.response?.data?.message || 'Could not load leads.')).finally(() => setLoading(false));
  };
  useEffect(load, [attribution]);

  const act = async (fn: () => Promise<any>, ok: string) => {
    try { await fn(); message.success(ok); load(); } catch (e: any) { message.error(e?.response?.data?.message || 'Action failed.'); }
  };

  const columns = [
    { title: 'Company', dataIndex: 'company_name', render: (v: string, r: api.PNLead) => (<div><Text strong>{v}</Text><div><Text type="secondary" style={{ fontSize: 12 }}>{r.country}{r.industry ? ` · ${r.industry}` : ''}</Text></div></div>) },
    { title: 'Channel', dataIndex: 'referrer_type', render: channelTag },
    { title: 'Contact', render: (_: unknown, r: api.PNLead) => <div><div>{r.contact_name}</div><Text type="secondary" style={{ fontSize: 12 }}>{r.contact_email}</Text></div> },
    { title: 'Stage', dataIndex: 'status', render: (v: string) => <Tag>{v.replace(/_/g, ' ').toLowerCase()}</Tag> },
    { title: 'Referral', dataIndex: 'attribution_status', render: (v: string, r: api.PNLead) => (<div><Tag color={attrColor[v] || 'default'}>{referralLabel(v)}</Tag>{r.attribution_expires_at ? <div><Text type="secondary" style={{ fontSize: 11 }}>exp {fmtDate(r.attribution_expires_at)}</Text></div> : null}</div>) },
    {
      title: 'Actions', render: (_: unknown, r: api.PNLead) => {
        const attr = r.attribution_status;
        return (
          <Space wrap>
            {attr === 'PENDING_REVIEW' || attr === 'DUPLICATE' ? (
              <>
                <Button size="small" type="primary" onClick={() => act(() => api.resolveAttribution(r.id, 'ACCEPTED'), 'Referral approved')}>Approve</Button>
                <Button size="small" danger onClick={() => act(() => api.resolveAttribution(r.id, 'REJECTED', 'reviewed'), 'Referral declined')}>Decline</Button>
              </>
            ) : null}
            {attr === 'ACCEPTED' && r.status !== 'WON' ? (
              <>
                <Select size="small" placeholder="Stage" style={{ width: 130 }} onChange={(v) => act(() => api.setLeadStage(r.id, v), 'Stage updated')}
                  options={['QUALIFIED', 'CONTACTED', 'DEMO_SCHEDULED', 'PROPOSAL', 'NEGOTIATION', 'LOST'].map((s) => ({ value: s, label: s.replace(/_/g, ' ').toLowerCase() }))} />
                <Button size="small" type="primary" onClick={() => { setConvert(r); cForm.resetFields(); }}>Convert…</Button>
              </>
            ) : null}
          </Space>
        );
      },
    },
  ];

  return (
    <>
      <Space style={{ marginBottom: 12 }}>
        <Select allowClear placeholder="Referral status" style={{ width: 200 }} value={attribution} onChange={setAttribution}
          options={Object.keys(attrColor).map((k) => ({ value: k, label: referralLabel(k) }))} />
        <Button icon={<ReloadOutlined />} onClick={load}>Refresh</Button>
      </Space>
      <Table rowKey="id" loading={loading} dataSource={rows} columns={columns as any} size="small" pagination={{ pageSize: 20 }} />

      <Modal title={`Convert ${convert?.company_name || ''}`} open={!!convert} onCancel={() => setConvert(null)}
        onOk={async () => { const v = await cForm.validateFields(); await act(() => api.convertLead(convert!.id, v), 'Converted — customer linked'); setConvert(null); }}>
        <Form form={cForm} layout="vertical">
          <Form.Item name="tenant_id" label="Customer tenant id" rules={[{ required: true }]} extra="The existing KaziPeople tenant this deal became.">
            <Input placeholder="uuid" />
          </Form.Item>
          <Form.Item name="complimentary_months" label="Complimentary launch (months)" extra="Configurable — 0 or blank = none. Commission never accrues during this period.">
            <InputNumber min={0} max={12} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

function RewardsTab() {
  const [rows, setRows] = useState<api.PNReward[]>([]);
  const [loading, setLoading] = useState(true);
  const load = () => { setLoading(true); api.listRewards().then(setRows).catch(() => undefined).finally(() => setLoading(false)); };
  useEffect(load, []);
  const act = async (fn: () => Promise<any>, ok: string) => { try { await fn(); message.success(ok); load(); } catch (e: any) { message.error(e?.response?.data?.message || 'Action failed.'); } };
  const columns = [
    { title: 'Referral', dataIndex: 'company_name' },
    { title: 'Type', dataIndex: 'referrer_type', render: channelTag },
    { title: 'Reward', render: (_: unknown, r: api.PNReward) => <Text>{r.reward_type}{r.reward_value ? ` · ${r.currency} ${r.reward_value.toLocaleString()}` : ''}</Text> },
    { title: 'Status', dataIndex: 'status', render: (v: string) => <Tag color={v === 'PAID' ? 'green' : v === 'REVERSED' ? 'red' : 'gold'}>{v.toLowerCase()}</Tag> },
    { title: 'Payout ref', dataIndex: 'downstream_payable_id', render: (v?: string) => <Text type="secondary" style={{ fontSize: 12 }}>{v || '—'}</Text> },
    {
      title: 'Actions', render: (_: unknown, r: api.PNReward) => (
        <Space>
          {(r.status === 'EARNED' || r.status === 'PENDING') && <Button size="small" type="primary" onClick={() => act(() => api.rewardAction(r.id, 'approve'), 'Approved — dispatched to payout')}>Approve</Button>}
          {r.status === 'PROCESSING' && <Button size="small" onClick={() => act(() => api.rewardAction(r.id, 'mark-paid'), 'Marked paid')}>Mark paid</Button>}
          {r.status !== 'REVERSED' && r.status !== 'PAID' && <Button size="small" danger onClick={() => act(() => api.rewardAction(r.id, 'reverse', 'reversed'), 'Reversed')}>Reverse</Button>}
        </Space>
      ),
    },
  ];
  return <Table rowKey="id" loading={loading} dataSource={rows} columns={columns as any} size="small" />;
}

function CommissionsTab() {
  const [rows, setRows] = useState<api.PNCommission[]>([]);
  const [loading, setLoading] = useState(true);
  const load = () => { setLoading(true); api.listCommissions().then(setRows).catch(() => undefined).finally(() => setLoading(false)); };
  useEffect(load, []);
  const act = async (fn: () => Promise<any>, ok: string) => { try { await fn(); message.success(ok); load(); } catch (e: any) { message.error(e?.response?.data?.message || 'Action failed.'); } };
  const columns = [
    { title: 'Partner', dataIndex: 'partner_id', render: (v: string) => <Text code>{v.slice(0, 8)}</Text> },
    { title: 'Eligible revenue', render: (_: unknown, r: api.PNCommission) => `${r.currency} ${r.eligible_revenue.toLocaleString()}` },
    { title: 'Rate', dataIndex: 'rate_percentage', render: (v?: number) => (v != null ? `${v}%` : '—') },
    { title: 'Commission', render: (_: unknown, r: api.PNCommission) => <Text strong>{r.currency} {r.amount.toLocaleString()}</Text> },
    { title: 'Paid month', dataIndex: 'paid_month_index', render: (v?: number) => v ?? '—' },
    { title: 'Status', dataIndex: 'status', render: (v: string) => <Tag color={v === 'PAID' ? 'green' : v === 'REVERSED' ? 'red' : v === 'APPROVED' ? 'blue' : 'gold'}>{v.toLowerCase()}</Tag> },
    {
      title: 'Actions', render: (_: unknown, r: api.PNCommission) => (
        <Space>
          {(r.status === 'CALCULATED' || r.status === 'PENDING') && <Button size="small" type="primary" onClick={() => act(() => api.commissionAction(r.id, 'approve'), 'Approved')}>Approve</Button>}
          {(r.status === 'APPROVED' || r.status === 'PAYABLE') && <Button size="small" onClick={() => act(() => api.commissionAction(r.id, 'mark-paid'), 'Marked paid')}>Mark paid</Button>}
          {r.status !== 'REVERSED' && r.status !== 'PAID' && <Button size="small" danger onClick={() => act(() => api.commissionAction(r.id, 'reverse', 'reversed'), 'Reversed')}>Reverse</Button>}
        </Space>
      ),
    },
  ];
  return <Table rowKey="id" loading={loading} dataSource={rows} columns={columns as any} size="small" />;
}

function AnalyticsTab() {
  const [data, setData] = useState<any>(null);
  useEffect(() => { api.getAnalytics().then(setData).catch(() => undefined); }, []);
  const rev: any[] = data?.attributed_revenue || [];
  return (
    <Row gutter={[16, 16]}>
      <Col span={6}><Card><Statistic title="Partners" value={data?.partners ?? 0} /></Card></Col>
      <Col span={6}><Card><Statistic title="Customers won" value={data?.customers_won ?? 0} /></Card></Col>
      <Col span={12}><Card><Text type="secondary">Referred collected revenue</Text><div>{rev.length ? rev.map((x) => <Tag key={x.currency}>{x.currency} {Number(x.amount).toLocaleString()}</Tag>) : <Text>—</Text>}</div></Card></Col>
    </Row>
  );
}

const planStatusColor = (s: string) => (s === 'active' ? 'green' : s === 'retired' ? 'default' : 'gold');

// PlansTab — the CONFIG surface: set the commission rate and the referral reward,
// and turn them on (draft → active). Nothing pays out until a plan is 'active'.
function PlansTab() {
  const [commission, setCommission] = useState<any[]>([]);
  const [reward, setReward] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [cEdit, setCEdit] = useState<any | null>(null); // commission plan being edited/created
  const [rEdit, setREdit] = useState<any | null>(null); // reward plan being edited/created
  const [cForm] = Form.useForm();
  const [rForm] = Form.useForm();

  const load = () => {
    setLoading(true);
    api.listPlans()
      .then((d) => { setCommission(d.commission_plans || []); setReward(d.reward_plans || []); })
      .catch((e) => message.error(e?.response?.data?.message || 'Could not load plans.'))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);
  const act = async (fn: () => Promise<any>, ok: string) => {
    try { await fn(); message.success(ok); load(); } catch (e: any) { message.error(e?.response?.data?.message || 'Action failed.'); }
  };

  const openCommission = (p: any | null) => {
    setCEdit(p || {});
    cForm.setFieldsValue(p
      ? { name: p.name, percentage: p.percentage, duration_value: p.duration_value }
      : { name: '', percentage: 20, duration_value: 12 });
  };
  const openReward = (p: any | null) => {
    setREdit(p || {});
    rForm.setFieldsValue(p
      ? { name: p.name, reward_type: p.reward_type, reward_value: p.reward_value, currency: p.currency, eligibility_trigger: p.eligibility_trigger }
      : { name: '', reward_type: 'FIXED_CASH', reward_value: undefined, currency: 'TZS', eligibility_trigger: 'FIRST_PAYMENT_COLLECTED' });
  };

  const commissionCols = [
    { title: 'Name', dataIndex: 'name', render: (v: string) => <Text strong>{v}</Text> },
    { title: 'Rate', dataIndex: 'percentage', render: (v: number) => (v == null ? '—' : `${v}%`) },
    { title: 'Duration', render: (_: unknown, r: any) => (r.duration_value ? `${r.duration_value} paid months` : '—') },
    { title: 'Partner type', dataIndex: 'partner_type' },
    { title: 'Status', dataIndex: 'status', render: (v: string) => <Tag color={planStatusColor(v)}>{v}</Tag> },
    {
      title: 'Actions', render: (_: unknown, r: any) => (
        <Space>
          <Button size="small" onClick={() => openCommission(r)}>Edit</Button>
          {r.status !== 'active'
            ? <Button size="small" type="primary" onClick={() => act(() => api.setPlanStatus('commission', r.id, 'active'), 'Plan activated — commissions will now calculate')}>Activate</Button>
            : <Button size="small" onClick={() => act(() => api.setPlanStatus('commission', r.id, 'draft'), 'Plan set to draft')}>Set draft</Button>}
        </Space>
      ),
    },
  ];
  const rewardCols = [
    { title: 'Name', dataIndex: 'name', render: (v: string) => <Text strong>{v}</Text> },
    { title: 'Reward', render: (_: unknown, r: any) => <Text>{r.reward_type}{r.reward_value != null ? ` · ${r.currency} ${Number(r.reward_value).toLocaleString()}` : ''}</Text> },
    { title: 'Trigger', dataIndex: 'eligibility_trigger', render: (v: string) => <Text type="secondary">{(v || '').replace(/_/g, ' ').toLowerCase()}</Text> },
    { title: 'Status', dataIndex: 'status', render: (v: string) => <Tag color={planStatusColor(v)}>{v}</Tag> },
    {
      title: 'Actions', render: (_: unknown, r: any) => (
        <Space>
          <Button size="small" onClick={() => openReward(r)}>Edit</Button>
          {r.status !== 'active'
            ? <Button size="small" type="primary" onClick={() => act(() => api.setPlanStatus('reward', r.id, 'active'), 'Reward plan activated')}>Activate</Button>
            : <Button size="small" onClick={() => act(() => api.setPlanStatus('reward', r.id, 'draft'), 'Reward plan set to draft')}>Set draft</Button>}
        </Space>
      ),
    },
  ];

  return (
    <>
      <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
        Commissions and rewards only pay out on an <b>active</b> plan. Set the rate/amount here, then Activate.
      </Typography.Paragraph>

      <Space style={{ marginBottom: 8 }}><Text strong>Commission plans</Text>
        <Button size="small" onClick={() => openCommission(null)}>+ New commission plan</Button>
        <Button size="small" icon={<ReloadOutlined />} onClick={load} />
      </Space>
      <Table rowKey="id" loading={loading} dataSource={commission} columns={commissionCols as any} size="small" pagination={false} style={{ marginBottom: 24 }} />

      <Space style={{ marginBottom: 8 }}><Text strong>Reward plans</Text>
        <Button size="small" onClick={() => openReward(null)}>+ New reward plan</Button>
      </Space>
      <Table rowKey="id" loading={loading} dataSource={reward} columns={rewardCols as any} size="small" pagination={false} />

      <Modal title={cEdit?.id ? 'Edit commission plan' : 'New commission plan'} open={!!cEdit} onCancel={() => setCEdit(null)}
        onOk={async () => {
          const v = await cForm.validateFields();
          await act(() => api.upsertCommissionPlan({ id: cEdit?.id, partner_type: cEdit?.partner_type || 'referral', duration_type: 'PAID_MONTHS', status: cEdit?.status || 'draft', ...v }), 'Commission plan saved');
          setCEdit(null);
        }}>
        <Form form={cForm} layout="vertical">
          <Form.Item name="name" label="Name" rules={[{ required: true }]}><Input placeholder="e.g. Referral partner default" /></Form.Item>
          <Form.Item name="percentage" label="Rate (% of collected revenue)" rules={[{ required: true }]}>
            <InputNumber min={0} max={100} step={0.5} style={{ width: '100%' }} addonAfter="%" />
          </Form.Item>
          <Form.Item name="duration_value" label="Duration (paid months)" rules={[{ required: true }]}>
            <InputNumber min={1} max={120} style={{ width: '100%' }} addonAfter="months" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal title={rEdit?.id ? 'Edit reward plan' : 'New reward plan'} open={!!rEdit} onCancel={() => setREdit(null)}
        onOk={async () => {
          const v = await rForm.validateFields();
          await act(() => api.upsertRewardPlan({ id: rEdit?.id, status: rEdit?.status || 'draft', ...v }), 'Reward plan saved');
          setREdit(null);
        }}>
        <Form form={rForm} layout="vertical">
          <Form.Item name="name" label="Name" rules={[{ required: true }]}><Input placeholder="e.g. Refer a friend — TZS 20,000" /></Form.Item>
          <Form.Item name="reward_type" label="Reward type" rules={[{ required: true }]}>
            <Select options={[
              { value: 'FIXED_CASH', label: 'Fixed cash' },
              { value: 'ACCOUNT_CREDIT', label: 'Account credit' },
              { value: 'PERCENTAGE', label: 'Percentage of revenue' },
              { value: 'VOUCHER', label: 'Voucher' },
              { value: 'POINTS', label: 'Points' },
            ]} />
          </Form.Item>
          <Form.Item name="reward_value" label="Amount / value"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="currency" label="Currency"><Input placeholder="TZS" /></Form.Item>
          <Form.Item name="eligibility_trigger" label="Pays out when">
            <Select options={[
              { value: 'CUSTOMER_SIGNED', label: 'Customer signs up' },
              { value: 'FIRST_PAYMENT_COLLECTED', label: 'Customer makes first payment' },
              { value: 'N_PAID_MONTHS', label: 'After N paid months' },
            ]} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

export default function PartnerNetworkPage() {
  return (
    <div style={{ padding: 24 }}>
      <Typography.Title level={3}>Partner Network</Typography.Title>
      <Typography.Paragraph type="secondary">Acquisition across both channels — professional partners and customer-user referrals — with referral approval, conversion and payout.</Typography.Paragraph>
      <Tabs items={[
        { key: 'leads', label: 'Leads & referrals', children: <LeadsTab /> },
        { key: 'plans', label: 'Plans (rates & rewards)', children: <PlansTab /> },
        { key: 'rewards', label: 'Referral rewards', children: <RewardsTab /> },
        { key: 'commissions', label: 'Commissions', children: <CommissionsTab /> },
        { key: 'analytics', label: 'Analytics', children: <AnalyticsTab /> },
      ]} />
    </div>
  );
}
